// Individual-level fatigue detail (FM-8 + FM-9), gated by useIndividualAccess (FM-10).
//
// One panel with two views rather than two stacked slide-overs: the list of
// people in the cohort (FM-8), and one person's breakdown reached by clicking a
// row (FM-9), with a back link. Stacking modals would trap focus twice over.
//
// The API enforces the same access rule this panel respects, so a 403 here means
// the UI offered something it shouldn't have — it is surfaced rather than
// swallowed.

import React from 'react';
import { ArrowLeft, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { FatigueFilters, IndividualFatigueRisk } from '../data/types';
import { useCohortIndividuals } from '../data/useFatigueData';
import { NOT_MEDICAL, RELIABLE_CONFIDENCE_MIN } from '../data/methodology';
import { LoadingBlock, NoteLine, StatRow, TierBadge, tierTextClass } from './primitives';
import { PanelSection, SidePanel } from './SidePanel';

const pct = (value: number | null) => (value === null ? '—' : `${value}%`);
const score = (value: number | null) => (value === null ? '—' : String(value));

// ── FM-8: the list ──────────────────────────────────────────────────────────

const IndividualRiskTable: React.FC<{
  people: IndividualFatigueRisk[];
  windowDays: number;
  onSelect: (personId: string) => void;
}> = ({ people, windowDays, onSelect }) => (
  <div className='overflow-x-auto'>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Person</TableHead>
          <TableHead className='text-right'>Score</TableHead>
          <TableHead className='text-right'>FED</TableHead>
          <TableHead className='text-right'>Sleep</TableHead>
          <TableHead className='text-right'>Days</TableHead>
          <TableHead>Tier</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {people.map((person) => (
          <TableRow key={person.id}>
            <TableCell className='font-medium'>
              <button
                type='button'
                onClick={() => onSelect(person.id)}
                className='rounded-sm text-left underline decoration-dotted underline-offset-4 hover:text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
              >
                {person.name}
              </button>
              <span className='block text-xs font-normal text-muted-foreground'>
                {person.jobRole ?? 'Role not recorded'}
                {person.dataConfidence < RELIABLE_CONFIDENCE_MIN && ' · limited data'}
              </span>
            </TableCell>
            <TableCell className={cn('text-right font-semibold tabular-nums', tierTextClass[person.riskTier])}>
              {score(person.fatigueScore)}
            </TableCell>
            <TableCell className='text-right tabular-nums'>{pct(person.fedPct)}</TableCell>
            <TableCell className='text-right tabular-nums'>
              {person.avgSleepHours === null ? '—' : `${person.avgSleepHours.toFixed(1)}h`}
            </TableCell>
            <TableCell className='text-right tabular-nums text-muted-foreground'>
              {person.dayCount}/{windowDays}
            </TableCell>
            <TableCell>
              <TierBadge tier={person.riskTier} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

// ── FM-9: one person ────────────────────────────────────────────────────────

const IndividualDetail: React.FC<{ person: IndividualFatigueRisk; windowDays: number }> = ({ person, windowDays }) => (
  <div className='flex flex-col'>
    <PanelSection title='Fatigue score'>
      <div className='flex items-baseline justify-between gap-3'>
        <span className={cn('text-3xl font-semibold tabular-nums', tierTextClass[person.riskTier])}>
          {score(person.fatigueScore)}
          <span className='ml-1 text-sm font-normal text-muted-foreground'>/ 100</span>
        </span>
        <TierBadge tier={person.riskTier} />
      </div>
      <p className='text-xs text-muted-foreground'>
        Weighted from their fatigue exposure, load–readiness misalignment and circadian disruption over the window, plus
        a fixed uplift when their own recovery signals are drifting together.
      </p>
    </PanelSection>

    <PanelSection title='Indicators'>
      <StatRow
        label='Fatigue exposure'
        value={pct(person.fedPct)}
        hint='Share of their measured days started with reduced readiness'
      />
      <StatRow
        label='Load–readiness misalignment'
        value={pct(person.lrmPct)}
        hint='Days where demand outran recovery'
      />
      <StatRow
        label='Circadian disruption'
        value={person.cdiScore === null ? '—' : `${person.cdiScore} / 100`}
        hint='Sleep timing consistency'
      />
      <StatRow
        label='Deviation cluster'
        value={person.inPdc === null ? 'Not enough history' : person.inPdc ? 'Yes' : 'No'}
        hint='Multiple recovery signals off their own baseline'
      />
    </PanelSection>

    <PanelSection title='Context'>
      <StatRow label='Job role' value={person.jobRole ?? 'Not recorded'} />
      <StatRow
        label='Average sleep'
        value={person.avgSleepHours === null ? '—' : `${person.avgSleepHours.toFixed(1)} hours`}
      />
      <StatRow
        label='Average readiness'
        value={person.avgReadiness === null ? '—' : `${person.avgReadiness.toFixed(0)} / 100`}
      />
      <StatRow label='Average strain' value={person.avgStrain === null ? '—' : person.avgStrain.toFixed(1)} />
      <StatRow
        label='Days with data'
        value={`${person.dayCount} of ${windowDays}`}
        hint={
          person.dataConfidence < RELIABLE_CONFIDENCE_MIN
            ? 'Below the reliable-data threshold — treat as indicative'
            : undefined
        }
      />
    </PanelSection>

    <PanelSection title='Scope'>
      <NoteLine>{NOT_MEDICAL}</NoteLine>
      <NoteLine>
        Individual fatigue exposure is a share of this person&apos;s own measured days, not the per-1,000-workdays rate
        used at cohort level, so the two figures are not directly comparable.
      </NoteLine>
    </PanelSection>
  </div>
);

// ── Panel ───────────────────────────────────────────────────────────────────

export const IndividualsPanel: React.FC<{
  cohortId: string | null;
  cohortName?: string;
  filters: FatigueFilters;
  /** FM-10 — false suppresses the request entirely; the API refuses it too. */
  allowed: boolean;
  onClose: () => void;
}> = ({ cohortId, cohortName, filters, allowed, onClose }) => {
  const [personId, setPersonId] = React.useState<string | null>(null);

  // Reset to the list whenever a different cohort is opened.
  React.useEffect(() => setPersonId(null), [cohortId]);

  const { individuals, window: apiWindow, isFetching, error } = useCohortIndividuals(cohortId, filters, allowed);
  const person = individuals?.find((p) => p.id === personId) ?? null;
  const windowDays = apiWindow?.days ?? 0;

  return (
    <SidePanel
      open={Boolean(cohortId)}
      onClose={onClose}
      width='lg'
      title={person ? person.name : `${cohortName ?? 'Cohort'} — individuals`}
      subtitle={
        person
          ? `${cohortName ?? ''} · individual fatigue detail`
          : 'Highest fatigue score first. Click a name for the full breakdown.'
      }
      footer={
        <>
          {person && (
            <Button variant='outline' size='sm' onClick={() => setPersonId(null)}>
              <ArrowLeft className='mr-1.5 size-4' aria-hidden />
              Back to list
            </Button>
          )}
          <Button variant='outline' size='sm' onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {error ? (
        <p className='text-sm text-muted-foreground'>
          Individual-level detail is not available for this client. Cohort figures cover the same period.
        </p>
      ) : isFetching && !individuals ? (
        <LoadingBlock className='h-64' />
      ) : person ? (
        <IndividualDetail person={person} windowDays={windowDays} />
      ) : individuals && individuals.length > 0 ? (
        <div className='flex flex-col gap-4'>
          <IndividualRiskTable people={individuals} windowDays={windowDays} onSelect={setPersonId} />
          <NoteLine>{NOT_MEDICAL}</NoteLine>
        </div>
      ) : (
        <p className='text-sm text-muted-foreground'>Nobody in this team has usable wearable data in this window.</p>
      )}
    </SidePanel>
  );
};

/** Locked state shown in place of the drill-down when access is denied (FM-10). */
export const IndividualAccessNotice: React.FC<{ reason: string }> = ({ reason }) => (
  <div className='flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-4'>
    <Lock className='mt-0.5 size-4 shrink-0 text-muted-foreground' aria-hidden />
    <div>
      <p className='text-sm font-medium'>Individual-level detail is locked</p>
      <p className='mt-1 text-sm text-muted-foreground'>{reason}</p>
    </div>
  </div>
);
